import { useEffect, useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Newspaper, Briefcase, Users, Plus, Pencil, Trash2, Search, Loader2, Save, X, Eye, EyeOff, Brain, Upload, FileText } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const inputCls = "bg-[#0b0811] border-white/10 text-white focus:border-[#f7c935]";

export default function ClubPage() {
  const { axiosAuth, isStaff } = useAuth();
  const api = axiosAuth();
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [business, setBusiness] = useState([]);
  const [reseaux, setReseaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState("article");
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // AI Article Generation
  const [showAI, setShowAI] = useState(false);
  const [aiFiles, setAiFiles] = useState([]);
  const [aiTopic, setAiTopic] = useState("");
  const [aiTone, setAiTone] = useState("professionnel");
  const [generatingAI, setGeneratingAI] = useState(false);
  const aiFileInputRef = useRef(null);

  const loadAll = async () => {
    try {
      const [a, b, r] = await Promise.all([
        api.get("/club/articles"),
        api.get("/club/business"),
        api.get("/club/reseaux"),
      ]);
      setArticles(a.data);
      setBusiness(b.data);
      setReseaux(r.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = (type) => {
    setDialogType(type);
    setForm({ status: "published" });
    setEditingId(null);
    setShowDialog(true);
  };

  const openEdit = (type, item) => {
    setDialogType(type);
    setForm({ ...item });
    setEditingId(item.article_id || item.business_id || item.reseau_id);
    setShowDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (dialogType === "article") {
        if (editingId) {
          await api.put(`/club/articles/${editingId}`, form);
        } else {
          await api.post("/club/articles", form);
        }
      } else if (dialogType === "business") {
        if (editingId) {
          await api.put(`/club/business/${editingId}`, form);
        } else {
          await api.post("/club/business", form);
        }
      } else if (dialogType === "reseau") {
        if (editingId) {
          await api.put(`/club/reseaux/${editingId}`, form);
        } else {
          await api.post("/club/reseaux", form);
        }
      }
      setShowDialog(false);
      loadAll();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm("Supprimer cet element ?")) return;
    try {
      if (type === "article") await api.delete(`/club/articles/${id}`);
      else if (type === "business") await api.delete(`/club/business/${id}`);
      else if (type === "reseau") await api.delete(`/club/reseaux/${id}`);
      loadAll();
    } catch {}
  };

  const handleAIFiles = (e) => {
    const files = Array.from(e.target.files || []);
    setAiFiles(prev => [...prev, ...files]);
    if (aiFileInputRef.current) aiFileInputRef.current.value = "";
  };

  const removeAIFile = (idx) => setAiFiles(aiFiles.filter((_, i) => i !== idx));

  const handleAIGenerate = async () => {
    if (!aiFiles.length) { alert("Ajoutez au moins un document source."); return; }
    setGeneratingAI(true);
    try {
      const fd = new FormData();
      aiFiles.forEach(f => fd.append("files", f));
      fd.append("topic", aiTopic || "");
      fd.append("tone", aiTone);
      const r = await api.post("/club/articles/generate", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      // Open editor on generated draft for review
      setShowAI(false);
      setAiFiles([]);
      setAiTopic("");
      setDialogType("article");
      setEditingId(r.data.article_id);
      setForm({ ...r.data });
      setShowDialog(true);
      loadAll();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || "Erreur lors de la generation IA");
    }
    setGeneratingAI(false);
  };

  const TABS = {
    articles: { items: articles, icon: Newspaper, label: "Articles", idKey: "article_id", type: "article" },
    business: { items: business, icon: Briefcase, label: "Business", idKey: "business_id", type: "business" },
    reseaux: { items: reseaux, icon: Users, label: "Reseaux d'affaires", idKey: "reseau_id", type: "reseau" },
  };

  const curr = TABS[tab];
  const filtered = (curr?.items || []).filter(i => (i.title || "").toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="club-page">
      <PageHeader
        icon={<Newspaper />}
        title="Club"
        description="Articles, business et réseaux d'affaires"
        testId="club-header"
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#14101c] border border-white/10 mb-6">
          {Object.entries(TABS).map(([k, v]) => (
            <TabsTrigger key={k} value={k} className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] gap-2" data-testid={`club-tab-${k}`}>
              <v.icon className="w-4 h-4" /> {v.label} ({v.items.length})
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-[#0b0811] border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]" />
          </div>
          {isStaff && (
            <>
              {tab === "articles" && (
                <Button onClick={() => { setAiFiles([]); setAiTopic(""); setAiTone("professionnel"); setShowAI(true); }} className="bg-[#461d8a] text-white hover:bg-[#5a2d9c] font-bold" data-testid="club-ai-generate-btn">
                  <Brain className="w-4 h-4 mr-2" /> Generer par IA
                </Button>
              )}
              <Button onClick={() => openCreate(curr.type)} className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="club-create-btn">
                <Plus className="w-4 h-4 mr-2" /> Creer
              </Button>
            </>
          )}
        </div>

        {/* Items */}
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <div className="bg-[#14101c] border border-white/10 rounded-xl p-12 text-center">
              <curr.icon className="w-10 h-10 mx-auto text-slate-600 mb-3" />
              <p className="text-slate-500">{isStaff ? "Aucun element. Cliquez sur \"Creer\" pour publier." : "Aucun contenu pour le moment."}</p>
            </div>
          ) : filtered.map((item) => (
            <div key={item[curr.idKey]} className="bg-[#14101c] border border-white/10 rounded-xl p-5 group" data-testid={`club-item-${item[curr.idKey]}`}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#461d8a20", color: "#5a2d9c" }}>
                  <curr.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{item.title}</h3>
                    {item.status === "draft" && <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-400">Brouillon</span>}
                    {item.category && <span className="text-xs text-slate-500">{item.category}</span>}
                  </div>
                  {item.content && <p className="text-sm text-slate-400 mt-1 line-clamp-2">{item.content}</p>}
                  {item.link && <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline mt-1 inline-block">{item.link}</a>}
                  <div className="text-xs text-slate-600 mt-2">Par {item.author_name || "Admin"} - {new Date(item.created_at).toLocaleDateString("fr-FR")}</div>
                </div>
                {isStaff && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button onClick={() => openEdit(curr.type, item)} className="p-1.5 rounded hover:bg-white/10"><Pencil className="w-4 h-4 text-slate-400" /></button>
                    <button onClick={() => handleDelete(curr.type, item[curr.idKey])} className="p-1.5 rounded hover:bg-red-500/10"><Trash2 className="w-4 h-4 text-red-400" /></button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingId ? "Modifier" : "Creer"} -
              {dialogType === "article" && " Article"}
              {dialogType === "business" && " Business"}
              {dialogType === "reseau" && " Reseau d'affaires"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input placeholder="Titre" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="club-form-title" />
            <Textarea placeholder="Contenu" value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={5} className={inputCls} data-testid="club-form-content" />
            <Input placeholder="Categorie" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
            {dialogType === "reseau" && (
              <Input placeholder="Lien (URL)" value={form.link || ""} onChange={(e) => setForm({ ...form, link: e.target.value })} className={inputCls} />
            )}
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Statut</label>
              <div className="flex gap-2">
                <button onClick={() => setForm({ ...form, status: "published" })}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${form.status === "published" ? "border-green-500 bg-green-500/10 text-green-400" : "border-white/10 text-slate-400"}`}
                  data-testid="club-status-published">
                  <Eye className="w-4 h-4 inline mr-1" /> Publie
                </button>
                <button onClick={() => setForm({ ...form, status: "draft" })}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm transition-colors ${form.status === "draft" ? "border-yellow-500 bg-yellow-500/10 text-yellow-400" : "border-white/10 text-slate-400"}`}
                  data-testid="club-status-draft">
                  <EyeOff className="w-4 h-4 inline mr-1" /> Brouillon
                </button>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving || !form.title} className="w-full bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] font-bold" data-testid="club-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingId ? "Modifier" : "Publier"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Article Generation Dialog */}
      <Dialog open={showAI} onOpenChange={(v) => { if (!generatingAI) setShowAI(v); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="club-ai-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#461d8a]" /> Generer un article par IA
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-xs text-slate-400">Importez plusieurs documents sources (textes, PDF, Word, images). L'IA extrait le contenu et redige un article complet selon un template unique (titre, chapeau, sections, points cles, conclusion).</p>

            <div>
              <label className="text-xs text-slate-500 mb-1.5 block font-semibold">Documents sources ({aiFiles.length})</label>
              <input ref={aiFileInputRef} type="file" multiple accept=".txt,.md,.csv,.html,.pdf,.doc,.docx,image/*" onChange={handleAIFiles} className="hidden" data-testid="club-ai-file-input" />
              <button onClick={() => aiFileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-white/10 hover:border-[#461d8a]/60 rounded-xl p-6 text-center transition-colors"
                data-testid="club-ai-upload-trigger">
                <Upload className="w-6 h-6 mx-auto text-slate-500 mb-2" />
                <div className="text-sm text-slate-300 font-semibold">Cliquez pour ajouter des fichiers</div>
                <div className="text-[11px] text-slate-500 mt-0.5">TXT, PDF, DOCX, images (OCR), max 10 MB par fichier</div>
              </button>
              {aiFiles.length > 0 && (
                <div className="mt-3 space-y-1.5 max-h-40 overflow-y-auto">
                  {aiFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 bg-[#0b0811] border border-white/5 rounded-lg px-3 py-2 text-xs" data-testid={`club-ai-file-${i}`}>
                      <FileText className="w-3.5 h-3.5 text-[#f7c935] flex-shrink-0" />
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-slate-500">{(f.size / 1024).toFixed(1)} KB</span>
                      <button onClick={() => removeAIFile(i)} className="p-0.5 hover:bg-red-500/10 rounded" data-testid={`club-ai-remove-${i}`}>
                        <X className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-slate-500 mb-1.5 block font-semibold">Sujet / angle (optionnel)</label>
              <Input placeholder="Ex: Comment utiliser l'IA pour booster son CA" value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} className={inputCls} data-testid="club-ai-topic" />
            </div>

            <div>
              <label className="text-xs text-slate-500 mb-1.5 block font-semibold">Ton de l'article</label>
              <Select value={aiTone} onValueChange={setAiTone}>
                <SelectTrigger className={inputCls} data-testid="club-ai-tone"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#14101c] border-white/10 text-white">
                  <SelectItem value="professionnel">Professionnel</SelectItem>
                  <SelectItem value="inspirant">Inspirant</SelectItem>
                  <SelectItem value="didactique">Didactique</SelectItem>
                  <SelectItem value="convivial">Convivial</SelectItem>
                  <SelectItem value="expert">Expert / technique</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAI(false)} disabled={generatingAI} className="flex-1 border-white/10 text-white hover:bg-white/5">Annuler</Button>
              <Button onClick={handleAIGenerate} disabled={generatingAI || aiFiles.length === 0} className="flex-1 bg-[#461d8a] text-white hover:bg-[#5a2d9c] font-bold" data-testid="club-ai-generate-submit">
                {generatingAI ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generation...</> : <><Brain className="w-4 h-4 mr-2" /> Generer l'article</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
